import React, { CSSProperties, useMemo, useRef } from 'react';
import { makeStyles } from '~/UIKit/Style';
import { ClickAwayListener, Fade, IconButton, Paper, Popper, PopperProps } from '@mui/material';
import { Close, InfoOutlined } from '@mui/icons-material';
import { PRIMARY_BLUE } from '~/Constants/Colors.constants';
import { FONT_SIZE_SMALLISH } from '~/Constants/UI.constants';
import cn from 'classnames';
import { useBoolean } from '~/Hooks';

const useStyles = ({ width }: CSSProperties) =>
  makeStyles(() => ({
    root: {
      zIndex: 1300,
    },
    inherit: {
      fontSize: 'inherit',
      color: 'inherit',
    },
    content: {
      width,
      padding: '23px',
      maxWidth: '100%',
      position: 'relative',
      fontSize: FONT_SIZE_SMALLISH,
      letterSpacing: '0.5px',
      lineHeight: '21px',
      textAlign: 'center',
      '& a': {
        cursor: 'pointer',
        textDecoration: 'none',
        color: PRIMARY_BLUE,
      },
    },
    withClose: {
      padding: '35px 24px 21px',
    },
    closeIcon: {
      width: 29,
      height: 29,
      color: PRIMARY_BLUE,
      position: 'absolute',
      top: 8,
      right: 10,
    },
  }));

export enum ICON_TOOLTIP_TYPE {
  INFO = 'info',
}

export enum ICON_TOOLTIP_TYPE_ACTION {
  CLICK = 'click',
  HOVER = 'hover',
}

type ITooltipProps = Omit<PopperProps, 'open'> & {
  iconType?: ICON_TOOLTIP_TYPE;
  typeAction?: ICON_TOOLTIP_TYPE_ACTION;
  children?: React.ReactNode;
  width?: number | string;
  tooltipIcon?: React.ReactNode;
};

export const IconTooltip = ({
  iconType = ICON_TOOLTIP_TYPE.INFO,
  placement = 'top',
  children = '',
  width = 280,
  typeAction = ICON_TOOLTIP_TYPE_ACTION.CLICK,
  tooltipIcon = null,
  ...baseProps
}: ITooltipProps) => {
  const { classes } = useStyles({ width })();
  const {
    state: open,
    setTrue: handleOpen,
    setFalse: handleClose,
    toggle: handleToggle,
  } = useBoolean(false);

  const buttonRef = useRef(null);

  const { buttonProps, tooltipProps } = useMemo(() => {
    switch (typeAction) {
      case ICON_TOOLTIP_TYPE_ACTION.CLICK:
        return {
          buttonProps: { onClick: handleToggle },
          tooltipProps: { onClose: handleClose },
        };
      case ICON_TOOLTIP_TYPE_ACTION.HOVER:
      default:
        return {
          buttonProps: {
            onClick: handleToggle,
            onMouseEnter: handleOpen,
            onMouseLeave: handleClose,
          },
        };
    }
  }, [typeAction, open]);

  const icon = useMemo(() => {
    switch (iconType) {
      case ICON_TOOLTIP_TYPE.INFO:
      default: {
        return <InfoOutlined className={classes.inherit} />;
      }
    }
  }, [iconType]);

  if (!children) {
    return null;
  }

  return (
    <>
      {buttonRef.current && (
        <Popper
          {...baseProps}
          {...tooltipProps}
          onResize={undefined}
          onResizeCapture={undefined}
          nonce={undefined}
          anchorEl={buttonRef.current}
          placement={placement}
          open={open}
          transition
          className={classes.root}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleClose}>
              <Fade {...TransitionProps} timeout={350}>
                <Paper
                  className={cn(classes.content, {
                    [classes.withClose]: typeAction === ICON_TOOLTIP_TYPE_ACTION.CLICK,
                  })}
                >
                  {typeAction === ICON_TOOLTIP_TYPE_ACTION.CLICK && (
                    <IconButton onClick={handleClose} className={classes.closeIcon} size="large">
                      <Close />
                    </IconButton>
                  )}
                  {children}
                </Paper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      )}
      <IconButton ref={buttonRef} {...buttonProps} className={classes.inherit} size="large">
        {tooltipIcon ?? icon}
      </IconButton>
    </>
  );
};
